/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text } from '@components/atoms'
import { Modal } from '@components/molecules'
import { TextField, MenuItem } from '@mui/material'
import { dealerPriceApi, wholesaleApi, ResType } from '@api'
import {
	Dealer,
	DealerType,
	WholesaleCatalogType,
	WholesaleReceiverType,
	emptyWholesaleReceiver,
} from '@schemas'
import { mono, boFont, boShared } from '@components/systems/backoffice/backoffice.tokens'

interface PropsType {
	open: boolean
	onClose: () => void
	/** 저장되면 주문번호를 돌려준다 — 부모가 목록을 다시 읽고 토스트를 띄운다 */
	onCreated: (orderId: string) => void
}

const money = (currency: string, value: number): string =>
	currency === 'KRW'
		? `₩${Math.round(value).toLocaleString('ko-KR')}`
		: `$${value.toLocaleString('en-US', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
		  })}`

const toNumber = (v: string): number => {
	const n = Number(String(v).replace(/,/g, ''))
	return Number.isNaN(n) || n < 0 ? 0 : n
}

/**
 * 도매 주문 대리 입력 (DISPATCH_PLAN §5.2) — 거래처가 카톡·전화로 준 주문을 관리자가 넣는다.
 * 사이트 주문과 같은 서버 함수(`POST /wholesale/admin/order/:customerId`)라 값 계산·재고 점유·상태가 같다.
 */
export const WholesaleOrderCreateModal = (props: PropsType): JSX.Element => {
	const [dealers, setDealers] = React.useState<DealerType[]>([])
	const [customerId, setCustomerId] = React.useState<string>('')
	const [catalog, setCatalog] = React.useState<WholesaleCatalogType | null>(null)
	const [isLoading, setIsLoading] = React.useState<boolean>(false)
	const [qty, setQty] = React.useState<{ [productId: string]: string }>({})
	const [receiver, setReceiver] = React.useState<WholesaleReceiverType>(
		emptyWholesaleReceiver(),
	)
	const [shipping, setShipping] = React.useState<string>('')
	const [duty, setDuty] = React.useState<string>('')
	const [memo, setMemo] = React.useState<string>('')
	const [error, setError] = React.useState<string>('')
	const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false)

	// 열릴 때 거래처 목록. 닫히면 전부 비운다
	React.useEffect(() => {
		if (!props.open) {
			setCustomerId('')
			setCatalog(null)
			setQty({})
			setReceiver(emptyWholesaleReceiver())
			setShipping('')
			setDuty('')
			setMemo('')
			setError('')
			return
		}
		let cancelled = false
		void (async () => {
			const res: ResType | undefined = await dealerPriceApi.getDealerList()
			if (cancelled) return
			if (res !== undefined && res.status === 200) {
				setDealers((res.data as any[]).map(a => new Dealer(a)))
			}
		})()
		return () => {
			cancelled = true
		}
	}, [props.open])

	// 거래처를 고르면 카탈로그(납품가·옵션·직전 수취인)
	React.useEffect(() => {
		if (customerId === '') {
			setCatalog(null)
			return
		}
		let cancelled = false
		setIsLoading(true)
		void (async () => {
			const res: ResType | undefined = await wholesaleApi.adminGetWholesaleCatalog({
				params: { customerId },
			})
			if (cancelled) return
			setIsLoading(false)
			if (res !== undefined && res.status === 200) {
				const data = res.data as unknown as WholesaleCatalogType
				setCatalog(data)
				setQty({})
				setReceiver(data.lastReceiver ?? emptyWholesaleReceiver())
				setError('')
			} else {
				setCatalog(null)
				setError('카탈로그를 읽지 못했습니다.')
			}
		})()
		return () => {
			cancelled = true
		}
	}, [customerId])

	const currency = catalog?.currency ?? 'USD'
	const lines = (catalog?.items ?? [])
		.map(anItem => ({
			...anItem,
			qty: Math.floor(toNumber(qty[anItem.productId] ?? '')),
		}))
		.filter(aLine => aLine.qty > 0)
	const subtotal: number = lines.reduce(
		(sum: number, aLine) => sum + Number(aLine.unitAmount) * Number(aLine.qty),
		0,
	)
	const vat =
		catalog?.vatExtraYn === 1 && currency === 'KRW' ? Math.round(subtotal * 0.1) : 0
	const shippingAmount: number = catalog?.shippingExtraYn === 1 ? toNumber(shipping) : 0
	const dutyAmount: number = catalog?.dutyExtraYn === 1 ? toNumber(duty) : 0
	const total: number = subtotal + vat + shippingAmount + dutyAmount

	const receiverOk =
		receiver.givenName.trim() !== '' &&
		receiver.phoneNumber.trim() !== '' &&
		receiver.street1.trim() !== '' &&
		receiver.countryName.trim() !== '' &&
		receiver.countryIsoCode.trim().length === 2
	const canSubmit = catalog !== null && lines.length > 0 && receiverOk && !isSubmitting

	const editReceiver = (key: keyof WholesaleReceiverType, value: string) =>
		setReceiver(prev => ({ ...prev, [key]: value }))

	const onSubmit = async () => {
		if (!canSubmit) return
		setIsSubmitting(true)
		setError('')
		const res: ResType | undefined = await wholesaleApi.adminCreateWholesaleOrder({
			params: { customerId },
			body: {
				items: lines.map(aLine => ({ productId: aLine.productId, qty: aLine.qty })),
				receiver: {
					...receiver,
					countryIsoCode: receiver.countryIsoCode.trim().toUpperCase(),
				},
				shippingAmount,
				dutyAmount,
				memo: memo.trim(),
			},
		})
		setIsSubmitting(false)
		if (res !== undefined && res.status < 400) {
			props.onCreated((res.data as any)?.orderId ?? '')
			props.onClose()
		} else {
			const data: any = res?.data
			const problems: any[] = data?.message?.meta?.detail?.problems ?? []
			setError(
				problems.length > 0
					? problems.map(p => p.message).join(' · ')
					: data?.message?.meta?.title ?? '저장하지 못했습니다.',
			)
		}
	}

	const rf = (key: keyof WholesaleReceiverType, label: string, required = false) => (
		<TextField
			key={key}
			label={`${label}${required ? ' *' : ''}`}
			value={receiver[key]}
			onChange={e => editReceiver(key, e.target.value)}
			size="small"
			fullWidth
		/>
	)

	return (
		<Modal
			show={props.open}
			target="wholesaleOrderCreate"
			title="도매 주문 추가"
			closeTitle="닫기"
			submitTitle={isSubmitting ? '저장 중…' : '주문 저장'}
			isSubmitDisabled={!canSubmit}
			isDisableDisimissionOnSubmit={true}
			onCloseModal={props.onClose}
			onSubmit={() => {
				void onSubmit()
			}}
		>
			<View css={style}>
				<Text>
					거래처가 카톡·전화로 준 주문을 대신 넣습니다. 값은 납품가 관리의 규칙, 재고는
					사이트 주문과 같은 식이고, 저장되면 입금 대기로 오더 관리에 섭니다.
				</Text>

				<TextField
					select
					label="거래처"
					value={customerId}
					onChange={e => setCustomerId(e.target.value)}
					size="small"
					fullWidth
				>
					{dealers.map(aDealer => (
						<MenuItem key={aDealer.customerId} value={aDealer.customerId}>
							{aDealer.companyName !== '' ? aDealer.companyName : aDealer.name} ·{' '}
							{aDealer.email} · {aDealer.currency}
						</MenuItem>
					))}
				</TextField>

				{isLoading && <span className="hint">읽는 중…</span>}

				{catalog !== null && catalog.seasonId === '' && (
					<span className="hint">열린 시즌이 없어 주문을 넣을 수 없습니다.</span>
				)}
				{catalog !== null && catalog.seasonId !== '' && catalog.items.length === 0 && (
					<span className="hint">
						이 거래처에 납품가가 잡힌 제품이 없습니다 — 납품가 관리에서 먼저.
					</span>
				)}

				{catalog !== null && catalog.items.length > 0 && (
					<>
						<View className="lines">
							<View className="lineRow head">
								<span>제품</span>
								<span className="right">납품가</span>
								<span className="right">수량</span>
								<span className="right">금액</span>
							</View>
							{catalog.items.map(anItem => {
								const q = Math.floor(toNumber(qty[anItem.productId] ?? ''))
								return (
									<View className="lineRow" key={anItem.productId}>
										<span>
											{anItem.name}{' '}
											<span className="code">
												{anItem.code} · {anItem.ruleLabel}
											</span>
										</span>
										<span className="right num">
											{money(currency, anItem.unitAmount)}
										</span>
										<span className="right">
											<TextField
												type="number"
												value={qty[anItem.productId] ?? ''}
												onChange={e =>
													setQty(prev => ({
														...prev,
														[anItem.productId]: e.target.value,
													}))
												}
												size="small"
												inputProps={{
													min: 0,
													step: 1,
													style: { textAlign: 'right', width: 60 },
												}}
											/>
										</span>
										<span className="right num">
											{q > 0 ? money(currency, anItem.unitAmount * q) : '—'}
										</span>
									</View>
								)
							})}
						</View>

						<View className="totals">
							<span>소계 {money(currency, subtotal)}</span>
							{catalog.vatExtraYn === 1 && currency === 'KRW' && (
								<span>부가세 {money(currency, vat)}</span>
							)}
							{catalog.shippingExtraYn === 1 && (
								<TextField
									label="배송비(별도)"
									value={shipping}
									onChange={e => setShipping(e.target.value)}
									size="small"
									inputProps={{ inputMode: 'decimal' }}
								/>
							)}
							{catalog.dutyExtraYn === 1 && (
								<TextField
									label="관세(별도)"
									value={duty}
									onChange={e => setDuty(e.target.value)}
									size="small"
									inputProps={{ inputMode: 'decimal' }}
								/>
							)}
							<span className="total">합계 {money(currency, total)}</span>
						</View>

						<span className="boKicker">수취인</span>
						<View className="receiverGrid">
							{rf('givenName', '이름', true)}
							{rf('familyName', '성')}
							{rf('phoneCountryCode', '국가번호')}
							{rf('phoneNumber', '전화', true)}
							{rf('street1', '주소', true)}
							{rf('street2', '주소 2')}
							{rf('city', '시')}
							{rf('stateName', '주/도')}
							{rf('zip', '우편번호')}
							{rf('countryName', '국가', true)}
							{rf('countryIsoCode', '국가 코드(ISO 2)', true)}
						</View>
						<TextField
							label="메모"
							value={memo}
							onChange={e => setMemo(e.target.value)}
							size="small"
							fullWidth
							inputProps={{ maxLength: 255 }}
						/>
					</>
				)}

				{error !== '' && <span className="error">{error}</span>}
			</View>
		</Modal>
	)
}

const style = css({
	...boShared,
	width: 'min(880px, 88vw)',
	display: 'flex',
	flexDirection: 'column',
	gap: 16,
	fontFamily: boFont.ui,

	'.hint': { fontSize: 12, color: mono.faint },
	'.error': { fontSize: 13, color: mono.error },

	'.lines': {
		borderTop: `1px solid ${mono.line}`,
		'.lineRow': {
			display: 'grid',
			gridTemplateColumns: 'minmax(0, 1fr) 120px 100px 130px',
			gap: 12,
			alignItems: 'center',
			padding: '8px 0',
			borderBottom: `1px solid ${mono.lineSoft}`,
			fontSize: 13,
			color: mono.ink,
			'&.head': {
				fontSize: 11,
				letterSpacing: '.12em',
				textTransform: 'uppercase',
				color: mono.faint,
				fontWeight: 500,
			},
			'.code': { fontSize: 11, color: mono.faint, marginLeft: 6 },
			'.right': { textAlign: 'right' },
			'.num': { fontVariantNumeric: 'tabular-nums' },
		},
	},

	'.totals': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		gap: 20,
		flexWrap: 'wrap',
		fontSize: 13,
		color: mono.dim,
		'.total': { fontSize: 15, fontWeight: 500, color: mono.ink },
	},

	'.receiverGrid': {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		gap: 12,
	},
})
